"use client"

import { Popover } from "@headlessui/react"
import { useParams, useRouter } from "next/navigation"
import React, { useMemo, useState } from "react"

import { useRouterScroll } from "@/hooks/useScroll"

import ArrowDown from "../../../../public/icons/arrow-down.svg"
import ArrowUp from "../../../../public/icons/arrow-up-dark.svg"
import FilterIcon from "../../../../public/icons/filter.svg"

import RadioButtons from "./RadioButtons"

interface Option {
  name: string
  slug: string
}

interface CategoryFilterProps {
  children?: React.ReactNode
  className?: string
  radioTitle?: string
  showingTextPrefix?: string
  options: Option[]
  scrollTo?: string
}

const CategoryFilter: React.FC<CategoryFilterProps> = ({
  children,
  className,
  radioTitle,
  showingTextPrefix = "Showing ",
  options = [],
  scrollTo = "",
}) => {
  const router = useRouter()
  const params = useParams()
  const { scrollToAfterNavigation } = useRouterScroll()

  const category = options?.some((option) => option?.slug === params.slug)
    ? (params.slug as string)
    : "all"

  const [selectedOption, setSelectedOption] = useState<string>(category)

  const showingText = useMemo(
    () => options?.find((item) => item?.slug === selectedOption)?.name,
    [options, selectedOption],
  )

  function onSelectHandler(selected) {
    setSelectedOption(selected)
    router.push(
      selected === "all" ? "/insights/" : `/insights/category/${selected}`,
      { scroll: false },
    )
  }

  const handleClick = () => {
    if (scrollTo) {
      scrollToAfterNavigation(scrollTo, {
        duration: 1000,
        delay: 200, // Increased delay for router navigation
        smooth: true,
      })
    }
  }

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <div className="border-b border-gray-75">
            <Popover.Button className="w-full p-4 inline-flex items-center caption1 text-gray-2.5 rounded-md group hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75">
              <FilterIcon className="mr-8" />
              {/* Have to cast to generic [key: string]: any to avoid indexing errors on union type */}
              <span className="caption1">{`${showingTextPrefix} ${showingText || "all"}`}</span>
              {open ? (
                <ArrowUp className="ml-4 md:ml-6" />
              ) : (
                <ArrowDown className="ml-4 md:ml-6" />
              )}
            </Popover.Button>
          </div>
          {/* The css is working correctly but you are not passing the className correctly so the classes
          for this component are not coming through
          If you hardcode in "bg-gray-90 shadow-filters" you will see that the styles work */}
          <Popover.Panel className={className}>
            {({ close }) => (
              <>
                <RadioButtons
                  close={close}
                  selected={selectedOption}
                  onSelected={(selected) => {
                    onSelectHandler(selected)
                    handleClick()
                  }}
                  options={options}
                  title={radioTitle}
                />
                {children}
              </>
            )}
          </Popover.Panel>
        </>
      )}
    </Popover>
  )
}

export default CategoryFilter
